import { createRoute, redirect, type AnyRootRoute, type AnyRoute } from '@tanstack/react-router'
import type { ComponentType, ReactNode } from 'react'
import { AppShellLayout } from '../../shared/layout/AppShellLayout'
import { requireAuth, AuthRedirect } from '../../shared/guards/requireAuth'
import { NotFoundPage } from '../../shared/layout/NotFoundPage'
import type { AuthApi } from '../../shared/auth/AuthContext'
import { RoleGate } from '../../shared/auth/RoleGate'
import { buildAdministrationRoutes } from '../administration/routes'
import { buildCalendarRoutes } from '../calendar/routes'
import { buildContactsRoutes } from '../contacts/routes'
import { buildDashboardRoutes } from '../dashboard/routes'
import { buildDocumentsRoutes } from '../documents/routes'
import { buildDroppingsRoutes } from '../droppings/routes'
import { buildMessagesRoutes } from '../messages/routes'
import { buildMiscRoutes } from '../misc/routes'
import { buildMyTeamRoutes } from '../my-team/routes'
import { buildNewsRoutes } from '../news/routes'
import { buildProfileRoutes } from '../profile/routes'
import { buildQuotasRoutes } from '../quotas/routes'
import { buildSeasonsRoutes } from '../seasons/routes'
import { buildObservationDaysRoutes } from '../observation-days/routes'

/** A route-builder mounted as a child of the authenticated app shell. Used by
 *  the composition root to inject another domain's routes (e.g. chronicle's
 *  statistics views) without clubhouse importing that domain (RULE-002). */
export type ShellRouteBuilder = (parent: AnyRoute) => AnyRoute[]

/** Wraps the shell's `<Outlet/>`. The composition root passes this to bridge a
 *  guest domain's auth context onto clubhouse's auth (e.g. chronicle). */
export type ShellOutletWrapper = ComponentType<{ auth: AuthApi; children: ReactNode }>

export interface AppShellRouteOptions {
  /** Extra route builders mounted under the authenticated shell. */
  extraRoutes?: ShellRouteBuilder[]
  /** Optional wrapper rendered around the shell's `<Outlet/>`. */
  outletWrapper?: ShellOutletWrapper
}

export function buildAppShellRoute<TRoot extends AnyRootRoute>(
  root: TRoot,
  options: AppShellRouteOptions = {},
) {
  const { extraRoutes = [], outletWrapper } = options

  const shellRoute = createRoute({
    getParentRoute: () => root,
    id: 'app',
    component: () => <AppShellLayout outletWrapper={outletWrapper} />,
    beforeLoad: async ({ location }) => {
      try {
        await requireAuth({ href: location.href })
      } catch (e) {
        if (e instanceof AuthRedirect) throw redirect({ to: e.to, search: e.search })
        throw e
      }
    },
  })

  const catchAllRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '*',
    component: NotFoundPage,
  })

  // Admin-only pages (Calendar, Messages, Seasons) live under a pathless gate
  // so a non-admin who types the URL hits a "not authorized" panel instead of
  // the page. The sidebar already hides these for members (roleVisibility).
  // RoleGate needs an explicit requiredRole, so the component is an arrow
  // wrapper; RoleGate renders <Outlet/> for the child page when allowed.
  const restrictedVisibilityGate = createRoute({
    getParentRoute: () => shellRoute,
    id: 'restricted-visibility-gate',
    component: () => <RoleGate requiredRole="Admin" />,
  })
  restrictedVisibilityGate.addChildren([
    ...buildCalendarRoutes(restrictedVisibilityGate),
    ...buildMessagesRoutes(restrictedVisibilityGate),
    ...buildSeasonsRoutes(restrictedVisibilityGate),
  ])

  return shellRoute.addChildren([
    ...buildAdministrationRoutes(shellRoute),
    ...buildObservationDaysRoutes(shellRoute),
    ...buildContactsRoutes(shellRoute),
    ...buildDashboardRoutes(shellRoute),
    ...buildDocumentsRoutes(shellRoute),
    ...buildDroppingsRoutes(shellRoute),
    ...buildMiscRoutes(shellRoute),
    ...buildMyTeamRoutes(shellRoute),
    ...buildNewsRoutes(shellRoute),
    ...buildProfileRoutes(shellRoute),
    ...buildQuotasRoutes(shellRoute),
    restrictedVisibilityGate,
    ...extraRoutes.flatMap((build) => build(shellRoute)),
    catchAllRoute,
  ])
}
